import { Close, FilterListOutlined } from "@mui/icons-material";
import {
  Box,
  Divider,
  FormControl,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DropdownFilter } from "@pimo/pimo-components";
import {
  DashboardFilterData,
  FUNCTION_TYPES,
  INITIATIVE_ENABLER_TYPES,
  LINES_OF_BUSINESS,
  Profile,
} from "aint-types";
import { Dayjs } from "dayjs";
import { FC } from "react";

export interface FilterDialogProps {
  onClick?: () => void;
  handleChange: (
    event: React.ChangeEvent<HTMLInputElement> | string,
    trigger: string
  ) => void;
  filterData: DashboardFilterData;
  userProfile?: Profile;
  filterOptions?: {
    oeProjects?: string[];
    functions?: string[];
    LoBs?: string[];
    programs?: string[];
    enablers?: string[];
    regions?: string[];
    implementationPrograms?: string[];
  };
}

export const FilterDialog: FC<FilterDialogProps> = ({
  onClick,
  handleChange,
  filterData,
  userProfile,
  filterOptions,
}) => {
  const theme = useTheme();

  const hasAllianzPartnersAccess =
    userProfile?.role === "editor" || userProfile?.role === "viewer"
      ? userProfile.oes?.some(
          (oe) => oe.operatingEntity === "Allianz Partners"
        ) ||
        userProfile.regions?.some((region) =>
          region.oes?.some((oe) => oe.operatingEntity === "Allianz Partners")
        )
      : false;

  const canSeeOtherFunctionOption =
    userProfile?.role === "admin" ||
    userProfile?.role === "globalViewer" ||
    hasAllianzPartnersAccess;

  const functionOptions = FUNCTION_TYPES.filter(
    (functionType) => functionType !== "Other" || canSeeOtherFunctionOption
  );

  const selectedFunctions = (filterData?.functionsFilter ?? []).filter(
    (functionType) => functionType !== "Other" || canSeeOtherFunctionOption
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 1,
        gap: 0,
        width: 300,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "start",
            width: "100%",
            pr: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              pr: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "start",
                width: "100%",
              }}
            >
              <FilterListOutlined />
              <Typography sx={{ fontWeight: 500 }}>Filter</Typography>
            </Box>
            <Close onClick={onClick} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
            <Box
              display="flex"
              sx={{ ".MuiFormControl-root": { width: "100%" } }}
              data-testid="DateFromField"
            >
              <FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      !filterData.fromDateFilter?.[0]
                        ? null
                        : filterData.fromDateFilter?.[0]
                    }
                    label={"From"}
                    views={["month", "year"]}
                    PaperProps={{
                      sx: {
                        ".Mui-selected": {
                          color: "#ffffff !important",
                        },
                      },
                    }}
                    onChange={(date: Dayjs | null) => {
                      if (!date) {
                        return;
                      }

                      date = date.startOf("month");

                      handleChange(date.toISOString(), "fromDateFilter");
                    }}
                    renderInput={(params: TextFieldProps) => (
                      <TextField {...params} />
                    )}
                  />
                </LocalizationProvider>
              </FormControl>
            </Box>
            <Box
              display="flex"
              sx={{ ".MuiFormControl-root": { width: "100%" } }}
              data-testid="DateUntilField"
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={
                    !filterData.untilDateFilter?.[0]
                      ? null
                      : filterData.untilDateFilter?.[0]
                  }
                  PaperProps={{
                    sx: {
                      ".Mui-selected": {
                        color: "#ffffff !important",
                      },
                    },
                  }}
                  label={"Until"}
                  views={["month", "year"]}
                  onChange={(date: Dayjs | null) => {
                    if (!date) {
                      return;
                    }

                    date = date.startOf("month");

                    handleChange(date.toISOString(), "untilDateFilter");
                  }}
                  renderInput={(params: TextFieldProps) => (
                    <TextField {...params} />
                  )}
                />
              </LocalizationProvider>
            </Box>
            {
              // Only show the OE filter if there are more than one OE projects and if no region filter is selected,
              // which will prevent users who do not have access to multiple OE projects from seeing the filter
            }
            {(filterOptions?.oeProjects?.length ?? 0) > 1 &&
              (filterData.regionsFilter?.length ?? 0) === 0 && (
                <DropdownFilter
                  options={filterOptions?.oeProjects ?? []}
                  value={filterData?.oeProjectsFilter ?? []}
                  label="OE"
                  handleChange={(e) => handleChange(e, "oeProjectsFilter")}
                  triggerIdentifier={"oeProjectsFilter"}
                />
              )}
            {
              // Only show region filter if no oe filter is selected, if there are more than one region.
            }
            {(filterOptions?.regions?.length ?? 0) > 1 &&
              (filterData.oeProjectsFilter?.length ?? 0) === 0 && (
                <DropdownFilter
                  options={filterOptions?.regions ?? []}
                  value={filterData?.regionsFilter ?? []}
                  label="Region"
                  handleChange={(e) => handleChange(e, "regionsFilter")}
                  triggerIdentifier={"regionsFilter"}
                />
              )}
            <DropdownFilter
              options={functionOptions.map((f) => f)}
              value={selectedFunctions}
              label="Function"
              handleChange={(e) => handleChange(e, "functionsFilter")}
              triggerIdentifier={"functionsFilter"}
            />
            <DropdownFilter
              options={LINES_OF_BUSINESS.map((l) => l)}
              value={filterData?.LoBsFilter ?? []}
              label="LoB"
              handleChange={(e) => handleChange(e, "LoBsFilter")}
              triggerIdentifier={"LoBsFilter"}
            />
            <DropdownFilter
              options={(filterOptions?.implementationPrograms ?? []).map(
                (e) => e
              )}
              value={filterData?.programsFilter ?? []}
              label="Implem. Program"
              handleChange={(e) => handleChange(e, "programsFilter")}
              triggerIdentifier={"programsFilter"}
            />
            <DropdownFilter
              options={INITIATIVE_ENABLER_TYPES.map((e) => e)}
              value={filterData?.enablersFilter ?? []}
              label="Enabler"
              handleChange={(e) => handleChange(e, "enablersFilter")}
              triggerIdentifier={"enablersFilter"}
            />
            <DropdownFilter
              options={["2020", "2021", "2022", "2023", "2024", "2025", "2026"]}
              value={filterData?.baselinesFilter ?? []}
              label="Baseline"
              handleChange={(e) => handleChange(e, "baselinesFilter")}
              triggerIdentifier={"baselinesFilter"}
            />
          </Box>
        </Box>
      </Box>
      <Divider
        sx={{
          bgcolor: theme.palette.secondary.main,
          height: "1px",
          borderWidth: 0,
        }}
      />
    </Box>
  );
};
